import React, { useEffect, useMemo } from 'react';
import cx from 'classnames';
import CheckBox from '../../../../components/input/CheckBox';
import Section from '../../../../components/outline/Section';
import InputForm from '../../../../components/form/InputForm';
import TextAreaForm from '../../../../components/form/TextAreaForm';
import Tabs from '../../../../components/Tabs';
import { asyncSimpleModal } from '../../../../lib/common';
import { formRequiredRules } from '../../../../lib/formRules';
import Button from '../../../../components/Button';
import {
  setAvailableTypeBtn,
  setDiscountTypeContent,
  setGenerateLimitTypeContent,
  setPeriodTypeContent,
} from '../../../../lib/utils/couponUtils';
import RadioGroupForm from '../../../../components/form/RadioGroupForm';
import {
  COUPON_AVAILABLE_TYPE,
  COUPON_AVAILABLE_TYPE_SELECT_LIST,
  COUPON_DISCOUNT_TYPE_SELECT_LIST,
  COUPON_DOWNLOAD_STATUS_TYPE,
  COUPON_DOWNLOAD_STATUS_TYPE_SELECT_LIST,
  COUPON_GENERATE_LIMIT_TYPE_SELECT_LIST,
  COUPON_PERIOD_TYPE,
  COUPON_PERIOD_TYPE_SELECT_LIST,
  COUPON_STATUS_TYPE_SELECT_LIST,
  DOWNLOADABLE_COUPON_NOTIFICATION,
  MAXIMUM_NUMBER_OF_COUPON_CODE,
  MAXIMUM_NUMBER_OF_COUPON_DESCRIPTION,
  MAXIMUM_NUMBER_OF_COUPON_MEMO,
  MAXIMUM_NUMBER_OF_COUPON_NAME,
} from '../../../../constants/service/app/couponConstants';
import { REX_COUPON_CODE } from '../../../../lib/rex';
import { REQUIRED_MSG } from '../../../../constants/service/common/formRulesConstants';
import BrandDiscountRateTabContent from './BrandDiscountRateTabContent';
import './CouponInfo.scss';

const CouponInfo = (props) => {
  const {
    form,
    setData,
    couponInfo,
    onGenerateLimitChange,
    onAvailableTypeChange,
    toggleModal,
    onPeriodTypeChange,
    setExclude24k,
    isEditable = true,
  } = props;

  // edit 페이지에서 브랜드 분담이 설정되어 있는지 확인
  const isEditPage = window.location.pathname.includes('/coupon/edit/');
  const hasSettlementShare = couponInfo.settlementShare && couponInfo.settlementShare.shareAmount > 0;
  const shouldDisableEditButton = isEditPage && hasSettlementShare;

  const brandDiscountRateType = couponInfo?.settlementShare ? couponInfo?.settlementShare.shareType : 'amount';

  // Tabs list 메모이제이션 - 불필요한 재렌더링 방지
  const discountTypeTabList = useMemo(
    () =>
      COUPON_DISCOUNT_TYPE_SELECT_LIST.map((item) => ({
        id: item.id,
        title: item.name,
        content: setDiscountTypeContent({
          type: item.id,
          discountAmount: couponInfo.discountAmount,
          minimumPurchasePrice: couponInfo.minimumPurchasePrice,
          maxDiscountPrice: couponInfo.maxDiscountPrice,
        }),
      })),
    [couponInfo.discountAmount, couponInfo.minimumPurchasePrice, couponInfo.maxDiscountPrice],
  );

  const periodTypeTabList = useMemo(
    () =>
      COUPON_PERIOD_TYPE_SELECT_LIST.map((item) => ({
        id: item.id,
        title: item.name,
        content: setPeriodTypeContent(form, setData, isEditable, {
          type: item.id,
          periodType: couponInfo.periodType,
          period: couponInfo.period,
          dayPlus: couponInfo.dayPlus,
        }),
        disabled: couponInfo.isDownloadable === COUPON_DOWNLOAD_STATUS_TYPE.ACTIVATE && item.name === '무기한',
      })),
    [
      form,
      setData,
      isEditable,
      couponInfo.periodType,
      couponInfo.period,
      couponInfo.dayPlus,
      couponInfo.isDownloadable,
    ],
  );

  const generateLimitTabList = useMemo(
    () =>
      COUPON_GENERATE_LIMIT_TYPE_SELECT_LIST.map((item) => ({
        id: item.id,
        title: item.name,
        content: setGenerateLimitTypeContent(form, setData, isEditable, {
          type: item.id,
          generateLimitType: couponInfo.generateLimitType,
          generateLimitCount: couponInfo.generateLimitCount,
          generateLimitDay: couponInfo.generateLimitDay,
        }),
      })),
    [
      form,
      setData,
      isEditable,
      couponInfo.generateLimitType,
      couponInfo.generateLimitCount,
      couponInfo.generateLimitDay,
    ],
  );

  const handleCheckExclude24k = async (checked) => {
    const modalData = {
      type: 'confirm',
    };

    if (checked) {
      modalData.title = '24K 상품 제외하기';
      modalData.message =
        '선택 시 24K 상품에 쿠폰 적용이 불가능하며, 브랜드 아이템 리스트가 초기화됩니다. 진행하시겠습니까?';
    } else {
      modalData.title = '24K 상품 제외하기 해제';
      modalData.message =
        '선택 해제 시 24K 상품에 쿠폰 적용이 가능하며 브랜드 아이템 리스트가 초기화됩니다. 진행하시겠습니까?';
    }

    const confirmResult = await asyncSimpleModal(modalData);

    if (confirmResult) {
      setExclude24k(checked);
    }
  };

  useEffect(() => {
    if (!form.isFieldTouched('isDownloadable')) {
      return;
    }
    if (couponInfo.isDownloadable === COUPON_DOWNLOAD_STATUS_TYPE.ACTIVATE) {
      asyncSimpleModal({
        type: 'info',
        title: '쿠폰 다운로드 변경하기',
        message: `다운로드 활성 시, '무기한 쿠폰'으로 생성이 불가합니다.`,
      }).then(() => {
        if (couponInfo.periodType === COUPON_PERIOD_TYPE.INFINITE) {
          onPeriodTypeChange(COUPON_PERIOD_TYPE.START_AND_END_DATE);
        }
      });
    }
  }, [couponInfo.isDownloadable]);

  return (
    <div className="coupon-info">
      {/*쿠폰 상태*/}
      <Section className="coupon-status" title="쿠폰 상태">
        <RadioGroupForm
          form={form}
          initialValue={couponInfo.status}
          onChange={(value) =>
            setData({
              path: ['status'],
              value,
            })
          }
          list={COUPON_STATUS_TYPE_SELECT_LIST}
          disabled={!isEditable}
          name="couponStatus"
        />
      </Section>
      {/*쿠폰다운로드*/}
      <Section
        className="coupon-download"
        title={
          <div>
            <h3 className="header">쿠폰 다운로드</h3>
            <span className="coupon-download-notification">{DOWNLOADABLE_COUPON_NOTIFICATION}</span>
          </div>
        }
      >
        <RadioGroupForm
          form={form}
          initialValue={couponInfo.isDownloadable || COUPON_DOWNLOAD_STATUS_TYPE.INACTIVE}
          onChange={(value) => {
            setData({
              path: ['isDownloadable'],
              value,
            });
          }}
          list={COUPON_DOWNLOAD_STATUS_TYPE_SELECT_LIST}
          disabled={!isEditable}
          name="isDownloadable"
        />
      </Section>

      {/*쿠폰정보*/}
      <Section className="coupon-description" title="쿠폰 정보">
        <div className="left">
          {/* 쿠폰명*/}
          <InputForm
            label="쿠폰명"
            form={form}
            rules={[formRequiredRules(true, REQUIRED_MSG)]}
            initialValue={couponInfo.name}
            maxLength={MAXIMUM_NUMBER_OF_COUPON_NAME}
            onChange={(value) =>
              setData({
                path: ['name'],
                value,
              })
            }
            isEditable={isEditable}
            name="name"
            subContent={
              isEditable && (
                <div className="sub-info">
                  <div>* 1~26자의 쿠폰 명을 입력해주세요.</div>
                </div>
              )
            }
          />

          {/* 쿠폰 설명*/}
          <InputForm
            label="쿠폰 설명"
            form={form}
            rules={[formRequiredRules(true, REQUIRED_MSG)]}
            initialValue={couponInfo.description}
            maxLength={MAXIMUM_NUMBER_OF_COUPON_DESCRIPTION}
            onChange={(value) =>
              setData({
                path: ['description'],
                value,
              })
            }
            isEditable={isEditable}
            name="description"
            subContent={
              isEditable && (
                <div className="sub-info">
                  <div>* 30자 이내의 쿠폰 설명을 입력해주세요.</div>
                </div>
              )
            }
          />
        </div>

        <div className="right">
          {/* 쿠폰메모*/}
          <TextAreaForm
            form={form}
            label="메모"
            initialValue={couponInfo.memo}
            maxLength={MAXIMUM_NUMBER_OF_COUPON_MEMO}
            onChange={(value) =>
              setData({
                path: ['memo'],
                value,
              })
            }
            name="memo"
            isEditable={isEditable}
            rows={4}
          />
        </div>
      </Section>

      {/*쿠폰 유형*/}
      <Section className="coupon-available-type" title="쿠폰 유형">
        <div className="btn-group">
          {COUPON_AVAILABLE_TYPE_SELECT_LIST.map((item, index) => (
            <div
              key={index}
              className={cx('type-btn', { active: item.id === couponInfo.availableType })}
              onClick={() => isEditable && onAvailableTypeChange(item.id)}
            >
              {setAvailableTypeBtn(item.id)}
              <br />
              {item.name}
            </div>
          ))}
        </div>
      </Section>

      {couponInfo.availableType === COUPON_AVAILABLE_TYPE.TARGET_BRAND_COUPON && (
        <div className="exclude24k">
          <CheckBox
            text={<strong>24K 상품 제외하기</strong>}
            checked={couponInfo.exclude24k}
            disabled={!isEditable}
            onChange={handleCheckExclude24k}
          />
          <p>* 체크 상태일 때 24K 상품에 쿠폰 적용이 불가능 합니다.</p>
        </div>
      )}

      {/*입력 코드*/}
      <Section className="coupon-code" title="입력 코드">
        {/* 입력 코드*/}
        <InputForm
          form={form}
          rules={[
            formRequiredRules(false, null, false),
            { pattern: REX_COUPON_CODE, message: '한글/영어(소문자)/숫자만 허용됩니다.' },
          ]}
          initialValue={couponInfo.code}
          maxLength={MAXIMUM_NUMBER_OF_COUPON_CODE}
          onChange={(value) =>
            setData({
              path: ['code'],
              value,
            })
          }
          isEditable={isEditable}
          name="code"
          subContent={
            isEditable && (
              <div className="sub-info">
                <div>* 입력코드를 입력하면 입력형 쿠폰이 생성됩니다.</div>
              </div>
            )
          }
        />
      </Section>

      {/*쿠폰 할인 타입*/}
      <Section
        className="coupon-discount-type tap-section"
        title={
          <div className="section-title-container">
            <div>쿠폰 할인 타입</div>

            <Button
              className="apply-discount-data-btn"
              color="primary"
              disabled={!isEditable || shouldDisableEditButton}
              onClick={() =>
                toggleModal({
                  name: 'editCouponDiscountModal',
                  value: true,
                  data: {
                    /**
                     *  현재 쿠폰의 유형
                     */
                    availableType: couponInfo.availableType,
                    /**
                     *  현재 쿠폰의 할인 타입
                     */
                    discountType: couponInfo.discountType,
                    /**
                     *  할인 타입에 따른 혜택 수치
                     */
                    discountAmount: couponInfo.discountAmount,
                    /**
                     *  최소 구매 금액
                     */
                    minimumPurchasePrice: couponInfo.minimumPurchasePrice,
                    /**
                     *  최대 할인 가능 금액
                     */
                    maxDiscountPrice: couponInfo.maxDiscountPrice,
                    /**
                     *  브랜드 분담 설정
                     */
                    settlementShare: couponInfo.settlementShare,
                    /**
                     *  edit 페이지 여부
                     */
                    isEditPage,
                  },
                })
              }
              text="편집하기"
            />
            {shouldDisableEditButton && (
              <div className="edit-disabled-warning">(!) 브랜드 분담이 있는 쿠폰은 수정이 불가능합니다</div>
            )}
          </div>
        }
      >
        <Tabs tabPosition="left" activeKey={couponInfo.discountType} list={discountTypeTabList} />
      </Section>

      <Section
        className="brand-discount-type tap-section"
        title={
          <span>
            <span>브랜드 분담{brandDiscountRateType === 'rate' ? '률' : '액'}</span>
          </span>
        }
      >
        <BrandDiscountRateTabContent
          brandDiscountRateType={brandDiscountRateType}
          couponDiscountAmount={couponInfo.discountAmount}
          brandShareAmount={couponInfo.settlementShare?.shareAmount}
          couponDiscountType={couponInfo.discountType}
        />
      </Section>

      {/*쿠폰 기간 설정*/}
      <Section className="coupon-period-type tap-section" title="쿠폰 기간 설정">
        <Tabs
          tabPosition="left"
          activeKey={couponInfo.periodType}
          onChange={(key) => isEditable && onPeriodTypeChange(key)}
          list={periodTypeTabList}
        />
      </Section>

      {/*제한 횟수 설정*/}
      <Section className="coupon-generate-limit-count tap-section" title="제한 횟수 설정">
        <Tabs
          tabPosition="left"
          activeKey={couponInfo.generateLimitType}
          onChange={(key) => isEditable && onGenerateLimitChange(key)}
          list={generateLimitTabList}
        />
      </Section>
    </div>
  );
};

export default CouponInfo;
